import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Linkedin, Twitter, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";
import { COMPANY, NAV_LINKS } from "../data/content";

export const Footer = () => {
  return (
    <footer data-testid="footer" className="relative bg-brand-ink text-white overflow-hidden">
      {/* 3D accent shapes */}
      <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand-green/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-brand-gold/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-10">
        {/* Top CTA strip */}
        <div className="border-b border-white/10 pb-16 mb-16 flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-4">Begin your transformation</div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-2xl">
              Partner with us to <em className="text-brand-gold not-italic font-serif italic">outperform</em> what's possible.
            </h2>
          </div>
          <Link
            to="/contact"
            data-testid="footer-cta"
            className="inline-flex items-center gap-3 bg-brand-gold text-brand-ink px-7 py-4 text-sm font-semibold rounded-sm hover:bg-white transition-colors whitespace-nowrap"
          >
            Start a Conversation <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <Logo variant="light" />
            <p className="mt-6 text-white/60 text-sm leading-relaxed max-w-sm">
              {COMPANY.legal}. {COMPANY.mission}
            </p>
            <div className="flex items-center gap-3 mt-6">
              <a href="#" data-testid="footer-linkedin" className="h-10 w-10 inline-flex items-center justify-center border border-white/20 rounded-sm hover:bg-brand-green hover:border-brand-green transition-colors"><Linkedin className="h-4 w-4" /></a>
              <a href="#" data-testid="footer-twitter" className="h-10 w-10 inline-flex items-center justify-center border border-white/20 rounded-sm hover:bg-brand-green hover:border-brand-green transition-colors"><Twitter className="h-4 w-4" /></a>
            </div>
          </div>

          {/* Nav */}
          <div className="md:col-span-3">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-5">Explore</div>
            <ul className="space-y-3">
              {NAV_LINKS.map(l => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-nav-${l.label.toLowerCase()}`} className="text-white/80 hover:text-brand-gold transition-colors">{l.label}</Link>
                </li>
              ))}
              <li>
                <Link to="/insights" data-testid="footer-nav-insights" className="text-white/80 hover:text-brand-gold transition-colors">Insights</Link>
              </li>
              <li>
                <Link to="/growth-readiness" data-testid="footer-nav-growth" className="text-white/80 hover:text-brand-gold transition-colors">Growth Readiness Index</Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-5">Contact</div>
            <ul className="space-y-4 text-white/80 text-sm">
              <li className="flex gap-3">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-brand-gold" />
                <span data-testid="footer-address">{COMPANY.address}</span>
              </li>
              <li className="flex gap-3">
                <Mail className="h-4 w-4 mt-0.5 flex-shrink-0 text-brand-gold" />
                <a data-testid="footer-email" href={`mailto:${COMPANY.email}`} className="hover:text-brand-gold">{COMPANY.email}</a>
              </li>
              <li className="flex gap-3">
                <Phone className="h-4 w-4 mt-0.5 flex-shrink-0 text-brand-gold" />
                <a data-testid="footer-phone" href={`tel:${COMPANY.phone.replace(/[^+\d]/g, '')}`} className="hover:text-brand-gold">{COMPANY.phone}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs text-white/40">
          <div>© {new Date().getFullYear()} {COMPANY.legal}. All rights reserved.</div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
